import moment from "moment";
import axios from "axios";
import { sanitizeToText } from "@/helpers/sanitize.helper.js";
const USERNAME_KEY = "username";

const COMPANYINFO_KEY = "companyInfo";
const USERDATA_KEY = "userdata";

const STYLESHEETID = "brandSpecificStyleSheet";

const ENVLS_KEY = "apiEnv";

// Pre-MFA token
let _preMfaToken = null;

/**
// The api uses startDate + number of nights instead of startdate/enddate. This function calculates the
// enddate based on the startdate and number of nights
 * @param {string} startDate - The start date in the format "YYYY-MM-DD".
 * @param {number} nights - The number of nights to include in the date range.
 * @returns {Object|null} An object containing the start date, end date, an array of dates, and formatted start and end date strings.
 *                        Returns null if the start date is not provided.
 * @property {string} startDate - The formatted start date.
 * @property {string} endDate - The formatted end date.
 * @property {Array<string>} dates - An array of formatted date strings for each night.
 * @property {string} startDateString - The start date formatted as "D MMMM" in Dutch locale.
 * @property {string} endDateString - The end date formatted as "D MMMM" in Dutch locale.
 */
const getDateRange = (startDate, nights) => {
  if (!startDate) {
    console.error("Please supply a startdate");
    return null;
  }
  startDate = moment(startDate, "YYYY-MM-DD");
  let endDate = moment(startDate).add(nights, "days");

  let dates = [];
  for (let i = 0; i < nights; i++) {
    let night = moment(startDate).add(i, "days").format();
    dates.push(night);
  }

  return {
    // The format is needed so it doesnt correct time 00:00 to day before
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: endDate.format("YYYY-MM-DD"),
    dates,
    startDateString: startDate.format("D MMMM", "nl"),
    endDateString: endDate.format("D MMMM", "nl")
  };
};

// These functions are used to store and retrieve data from localstorage, mainly preventing errors
const storageSetObject = (userData) => {
  let jsonString = JSON.stringify(userData);
  localStorage.setItem(USERDATA_KEY, jsonString);
};
const storageGetObject = () => {
  if (localStorage.getItem(USERDATA_KEY)) {
    return JSON.parse(localStorage.getItem(USERDATA_KEY));
  } else {
    return {};
  }
};

const storageGetValue = (key) => {
  let userData = storageGetObject();
  return userData ? userData[key] : null;
};
const storageSetValue = (key, value) => {
  let userData = storageGetObject();
  if (!userData) {
    userData = {};
  }
  userData[key] = value;

  storageSetObject(userData);
};

// This method clears the storage, but keeps the api environment if thats set
const clearStorage = () => {
  const apiEnv = localStorage.getItem(ENVLS_KEY);

  localStorage.clear();
  _preMfaToken = null;
  if (apiEnv) {
    localStorage.setItem(ENVLS_KEY, apiEnv);
  }
};

const saveSession = (userName) => {
  localStorage.setItem(USERNAME_KEY, userName);
};
const saveToken = (token) => {
  _preMfaToken = token;
};

const clearToken = () => {
  _preMfaToken = null;
};

const saveCompanyInfo = (data) => {
  localStorage.setItem(COMPANYINFO_KEY, JSON.stringify(data));
};

const hasCompanyInfo = () => {
  let hasCompanyInfo = !!localStorage.getItem(COMPANYINFO_KEY);
  return hasCompanyInfo;
};

// This method creates a hash based on the current date and time of day which is based on the 12 hour clock, so it can be used to check if the data is still valid/recent
// Basically it makes sure the data is not older than a day
const getDateHash = () => {
  const timeOfDay = new Date().getHours() > 12 ? "pm" : "am";
  return (
    new Date().getFullYear() +
    "" +
    (new Date().getMonth() + 1) +
    "" +
    new Date().getDate() +
    "" +
    timeOfDay
  );
};
const fetchCompanyInfo = async () => {
  let url = `${getApiURL()}/company/company-info`;
  let { data } = await axios.get(url);
  if (data) {
    saveCompanyInfo(data);
    localStorage.ciDate = getDateHash();
    return data;
  } else {
    return null;
  }
};

/**
 * Fetch visa information for a given country ID.
 *
 * @param {Number} countryId - The ID of the country.
 * @returns {Promise<Object>} - The visa information for the country.
 */
export const fetchVisaInfo = async (countryId) => {
  try {
    const url = `${getApiURL()}/country/get?id=${countryId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching visa information:", error);
    throw error; // Re-throw the error to handle it in the calling code
  }
};

const getCompanyInfo = async () => {
  let companyData;
  if (localStorage.getItem(COMPANYINFO_KEY)) {
    companyData = JSON.parse(localStorage.getItem(COMPANYINFO_KEY));
  } else {
    companyData = await fetchCompanyInfo();
  }

  const hasCompanyObject =
    companyData && typeof companyData === "object" && companyData.company;

  if (!hasCompanyObject) {
    const refreshedCompanyData = await fetchCompanyInfo();
    if (refreshedCompanyData && typeof refreshedCompanyData === "object") {
      companyData = refreshedCompanyData;
    }
  }

  return companyData;
};

const getCompanyInfoSync = () => {
  if (localStorage.getItem(COMPANYINFO_KEY)) {
    return JSON.parse(localStorage.getItem(COMPANYINFO_KEY));
  } else {
    return {};
  }
};

const getBusinessUnits = async () => {
  let companyInfo = await getCompanyInfo();
  let businessUnits = companyInfo.businessUnits;

  companyInfo.businessUnits.forEach((bU) => {
    businessUnits.push(bU.businessUnits);
  });
  let allBusinessUnits = businessUnits.flat();

  return allBusinessUnits;
};

// The annotations can be used to show additional information above the search results.
// This function retrieves the annotations in the correct language
const getCompanyAnnotations = async () => {
  const languageIso = localStorage.locale || "NL";
  let companyInfo = await getCompanyInfo();

  // Safety check: ensure annotations exists and is an array
  if (
    !companyInfo ||
    !companyInfo.annotations ||
    !Array.isArray(companyInfo.annotations)
  ) {
    return [];
  }

  let annotations = companyInfo.annotations.filter(
    (a) => a.languageIso === languageIso.toUpperCase()
  );
  return annotations;
};

/**
 * This is a helper to get locations/hotels from user input
 *
 * @param {string} searchInput - The input string to search for locations.
 * @returns {Promise<Object>} A promise that resolves to the location data.
 */
const getLocationsFromSearch = async (searchInput) => {
  const languageIso = (localStorage.locale || "nl").toLowerCase();
  let url = `${getApiURL()}/autocomplete/geopath/${languageIso}/${searchInput}/?amount=70`;
  const { data } = await axios.get(url);
  return data;
};

// This function checks if the session is still valid by calling the api and judging the response
const sessionIsValid = async () => {
  const url = `${getApiURL()}/authentication/validate`;

  try {
    let { data: validationData } = await axios.post(url);
    if (validationData.length && validationData[0].message) {
      return false;
    } else {
      return true;
    }
  } catch (e) {
    return false;
  }
};

/**
 * This function invalidates the session, which means it logs out the user and clears the storage and stylesheets.
 * Usually triggered by a 401 error
 *
 * @async
 * @function invalidateSession
 * @param {boolean} [skipLogout=false] - If true, skips the logout process (used when assets give errors, usually those are stacked errors and can be ignored)
 * @returns {Promise<void>} - A promise that resolves when the session is invalidated.
 */
const invalidateSession = async (skipLogout = false) => {
  if (!skipLogout && isLoggedIn()) {
    const url = `${getApiURL()}/authentication/logout`;
    try {
      await axios.post(url);
    } catch (e) {
      console.error("Error logging out", e);
    }
  }
  clearStorage();

  if (document.getElementById(STYLESHEETID)) {
    document.getElementById(STYLESHEETID).remove();
  }
  return;
};

const isLoggedIn = () => {
  if (localStorage.getItem(USERNAME_KEY) !== null) {
    return true;
  } else {
    return false;
  }
};

const getToken = () => {
  return _preMfaToken;
};

const hasToken = () => {
  return _preMfaToken !== null;
};

const formatNumber = (number) => {
  return new Intl.NumberFormat("nl-NL").format(number);
};

const formatDecimals = (number) => {
  return new Intl.NumberFormat("nl-NL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(number);
};

const formatPrice = (number) => {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR"
  }).format(number);
};

const stripHtml = (value = "") => {
  if (value == null) {
    return "";
  }

  const htmlString = String(value);
  if (!htmlString) {
    return "";
  }

  return sanitizeToText(htmlString)
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// eslint-disable-next-line no-undef
const localApiUrl = `${process.env.VUE_APP_AVAILABILITY_API_URL_LOCAL}`;
// eslint-disable-next-line no-undef
const devTestApiUrl = `${process.env.VUE_APP_AVAILABILITY_API_URL_DEVTEST}`;
// eslint-disable-next-line no-undef
const testApiUrl = `${process.env.VUE_APP_AVAILABILITY_API_URL_TEST}`;
// eslint-disable-next-line no-undef
const stagingApiUrl = `${process.env.VUE_APP_AVAILABILITY_API_URL_STAGING}`;
// eslint-disable-next-line no-undef
const prodApiUrl = `${process.env.VUE_APP_AVAILABILITY_API_URL_PROD}`;

// You can use env=staging or env=test in the url to switch the api environment
// The used API is stored in the localstorage so it persists
const checkEnvFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams && urlParams.get("env")) {
    const envParamValue = urlParams.get("env");
    localStorage.setItem(ENVLS_KEY, envParamValue);
  }
};

/**
 * Determines the API environment based on the current URL and localstorage settings.
 *
 * @returns {string} The API environment, either "prod" or "test".
 */
const getApiEnv = () => {
  const prodUrl = "https://portal.lookingforbooking.com";
  var currentUrl = window.location.origin;

  let apiEnv = "test";
  if (localStorage.getItem(ENVLS_KEY) == null) {
    if (currentUrl == prodUrl) {
      apiEnv = "prod";
    } else {
      apiEnv = "test";
    }
  } else {
    apiEnv = localStorage.getItem(ENVLS_KEY) ?? "prod";
  }
  return apiEnv;
};

const isProd = () => getApiEnv() === "prod";

/**
 * Read the api env from the localstorage and return the correct api url
 *
 * This function determines the environment by calling `getApiEnv()` and returns
 * the corresponding API URL. The possible environments are "staging", "test",
 * "devtest", and the default environment which is assumed to be "production".
 *
 * @returns {string} The API URL corresponding to the current environment.
 */
const getApiURL = () => {
  checkEnvFromUrl();
  let apiUrl;
  const apiEnv = getApiEnv();
  switch (apiEnv) {
    case "staging":
      apiUrl = stagingApiUrl;
      break;
    case "test":
      apiUrl = testApiUrl;
      break;
    case "devtest":
      apiUrl = devTestApiUrl;
      break;
    case "local":
      apiUrl = localApiUrl;
      break;
    default:
      apiUrl = prodApiUrl;
  }

  return apiUrl;
};

// For consisntency, the pages are stored in a constant so naming/renaming is easier and
// less error prone, plus it makes it easier to find all pages in the code
const PAGES = {
  Login: "login",
  SSO: "SSOPage",
  MFA: "MFAForm",
  UserProfile: "UserProfile",
  FlightForm: "FlightForm",
  LandingPage: "landingPage",
  SearchResult: "searchResult",
  ReservationOverview: "reservationOverview",
  HotelDetail: "hotelDetail",
  HotelReservationForm: "hotelreservation",
  HotelRebook: "hotelrebook",
  CityReservationForm: "cityreservation",
  MeetingRoomReservation: "meetingroomreservation",
  OpenRequest: "openRequest",
  TrainForm: "trainform",
  CarForm: "carform",
  PartialError: "partialerror",
  BookingRules: "bookingRules"
};

const GEO_LOCATION_TYPES = {
  ALL: "all",
  HOTEL: "hotel",
  CITY: "city",
  AIRPORT: "airport"
};
const MeetingRoomSelectables = {
  selectableFacilities: [
    "Beamer/Scherm",
    "Flipover",
    "Microfoon",
    "Geluidsinstallatie"
  ],
  selectableCatering: [
    "Koffie / Thee / Water",
    "Lunch",
    "Diner",
    "Frisdranken",
    "Borrel"
  ],
  roomLayouts: [
    "U-Shape",
    "Cabaret",
    "Square",
    "Theater",
    "Block",
    "School",
    "Boardroom",
    "Other"
  ]
};

const nationalities = [
  "Nederlandse",
  "Slowaakse",
  "Tsjechische",
  "Burger van Bosnië-Herzegovina",
  "Georgische",
  "Turkmeense",
  "Tadzjiekse",
  "Oezbeekse",
  "Oekraïense",
  "Kirgizische",
  "Moldavische",
  "Kazachse",
  "Belarussische",
  "Azerbeidzjaanse",
  "Armeense",
  "Russische",
  "Sloveense",
  "Kroatische",
  "Letse",
  "Estische",
  "Litouwse",
  "Marshalleilandse",
  "Myanmarese",
  "Namibische",
  "Albanese",
  "Andorrese",
  "Belgische",
  "Bulgaarse",
  "Deense",
  "Burger van de Bondsrepubliek Duitsland",
  "Finse",
  "Franse",
  "Jemenitische",
  "Griekse",
  "Brits burger",
  "Hongaarse",
  "Ierse",
  "IJslandse",
  "Italiaanse",
  "Joegoslavische",
  "Liechtensteinse",
  "Luxemburgse",
  "Maltese",
  "Monegaskische",
  "Noorse",
  "Oostenrijkse",
  "Poolse",
  "Portugese",
  "Roemeense",
  "Burger van de Sovjet-Unie",
  "San Marinese",
  "Spaanse",
  "Tsjecho-Slowaakse",
  "Vaticaanse",
  "Zweedse",
  "Zwitserse",
  "Oost-Duitse",
  "Brits onderdaan",
  "Eritrese",
  "Brits overzees burger",
  "Macedonische",
  "Kosovaarse",
  "Macedonische/Burger van Noord-Macedonië",
  "Algerijnse",
  "Angolese",
  "Burundese",
  "Botswaanse",
  "Burkinese",
  "Centraal-Afrikaanse",
  "Comorese",
  "Burger van Congo",
  "Beninse",
  "Egyptische",
  "Equatoriaal-Guinese",
  "Ethiopische",
  "Djiboutiaanse",
  "Gabonese",
  "Gambiaanse",
  "Ghanese",
  "Guinese",
  "Ivoriaanse",
  "Kaapverdische",
  "Kameroense",
  "Kenyaanse",
  "Zaïrese",
  "Lesothaanse",
  "Liberiaanse",
  "Libische",
  "Malagassische",
  "Malawische",
  "Malinese",
  "Marokkaanse",
  "Mauritaanse",
  "Mauritiaanse",
  "Mozambikaanse",
  "Swazische",
  "Nigerese",
  "Nigeriaanse",
  "Ugandese",
  "Guinee-Bissause",
  "Zuid-Afrikaanse",
  "Eswatinische",
  "Zimbabwaanse",
  "Rwandese",
  "Burger van São Tomé en Principe",
  "Senegalese",
  "Sierra Leoonse",
  "Soedanese",
  "Somalische",
  "Tanzaniaanse",
  "Togolese",
  "Tsjadische",
  "Tunesische",
  "Zambiaanse",
  "Zuid-Soedanese",
  "Bahamaanse",
  "Belizaanse",
  "Canadese",
  "Costa Ricaanse",
  "Cubaanse",
  "Dominicaanse",
  "Salvadoraanse",
  "Guatemalaanse",
  "Haïtiaanse",
  "Hondurese",
  "Jamaicaanse",
  "Mexicaanse",
  "Nicaraguaanse",
  "Panamese",
  "Burger van Trinidad en Tobago",
  "Amerikaans burger",
  "Argentijnse",
  "Barbadaanse",
  "Boliviaanse",
  "Braziliaanse",
  "Chileense",
  "Colombiaanse",
  "Ecuadoraanse",
  "Guyaanse",
  "Paraguayaanse",
  "Peruaanse",
  "Surinaamse",
  "Uruguayaanse",
  "Venezolaanse",
  "Grenadaanse",
  "Burger van Saint Kitts en Nevis",
  "Afghaanse",
  "Bahreinse",
  "Bhutaanse",
  "Burmaanse",
  "Bruneise",
  "Cambodjaanse",
  "Sri Lankaanse",
  "Chinese",
  "Cyprische",
  "Filipijnse",
  "Taiwanese",
  "Indiase",
  "Indonesische",
  "Iraakse",
  "Iraanse",
  "Israëlische",
  "Japanse",
  "Noord-Jemenitische",
  "Jordaanse",
  "Koeweitse",
  "Laotiaanse",
  "Libanese",
  "Maldivische",
  "Maleisische",
  "Mongolische",
  "Omaanse",
  "Nepalese",
  "Noord-Koreaanse",
  "Pakistaanse",
  "Qatarese",
  "Saoedi-Arabische",
  "Singaporese",
  "Syrische",
  "Thaise",
  "Burger van de Verenigde Arabische Emiraten",
  "Turkse",
  "Zuid-Jemenitische",
  "Zuid-Koreaanse",
  "Vietnamese",
  "Bengalese",
  "Australische",
  "Papoea-Nieuw-Guinese",
  "Nieuw-Zeelandse",
  "West-Samoaanse",
  "Samoaanse",
  "Burger van Antigua en Barbuda",
  "Vanuatuaanse",
  "Fijische",
  "Burger van Britse afhankelijke gebieden",
  "Tongaanse",
  "Nauruaanse",
  "Palause",
  "Amerikaans onderdaan",
  "Salomonseilandse",
  "Micronesische",
  "Seychelse",
  "Kiribatische",
  "Tuvaluaanse",
  "Saint Luciaanse",
  "Burger van Dominica",
  "Burger van Saint Vincent en de Grenadines",
  "British National (overseas)",
  "Burger van Democratische Republiek Congo",
  "Burger van Timor Leste",
  "Burger van Servië en Montenegro",
  "Servische",
  "Montenegrijnse"
];

/**
 * Get the icon class based on the feature code.
 *
 * @param {string} featureCode - The feature code to determine the icon for.
 * @returns {string} The icon class corresponding to the feature code.
 */
export function getFeatureCodeIcon(featureCode) {
  switch (featureCode) {
    case "HTL":
      return "fas fa-bed";
    case "aerodrome":
      return "fas fa-plane";
    case "train_station":
      return "fas fa-train";
    case "bus_station":
      return "fas fa-bus";
    case "ferry_terminal":
      return "fas fa-ship";
    case "taxi_stand":
      return "fas fa-taxi";
    case "transport_hub":
      return "fas fa-car";
    case "gas_station":
      return "fas fa-gas-pump";
    case "automotive":
      return "fas fa-car";
    case "charging_station":
      return "fas fa-charging-station";
    case "parking":
      return "fas fa-square-parking";
    case "park":
      return "fas fa-tree";
    case "tourist_attraction":
      return "fas fa-camera";
    case "sports_facility":
    case "sports_activity":
      return "fas fa-dumbbell";
    case "entertainment":
      return "fas fa-masks-theater";
    case "recreation":
      return "fas fa-person-running";
    case "museum":
      return "fas fa-landmark";
    case "theater":
      return "fas fa-theater-masks";
    case "university":
      return "fas fa-graduation-cap";
    case "library":
      return "fas fa-book";
    case "school":
      return "fas fa-school";
    case "hospital":
      return "fas fa-hospital";
    case "pharmacy":
      return "fas fa-pills";
    case "medical":
      return "fas fa-user-doctor";
    case "wellness":
      return "fas fa-spa";
    case "government":
      return "fas fa-building-columns";
    case "police":
      return "fas fa-shield-halved";
    case "fire_station":
      return "fas fa-fire-flame-curved";
    case "post_office":
      return "fas fa-envelope";
    case "place_of_worship":
      return "fas fa-place-of-worship";
    case "restaurant":
      return "fas fa-utensils";
    case "food_specialty":
      return "fas fa-mug-hot";
    case "food_service":
      return "fas fa-truck-fast";
    case "shopping_mall":
      return "fas fa-store";
    case "grocery":
      return "fas fa-cart-shopping";
    case "retail":
      return "fas fa-shopping-bag";
    case "bank":
      return "fas fa-building-columns";
    case "atm":
      return "fas fa-credit-card";
    case "finance":
      return "fas fa-dollar-sign";
    case "beauty_service":
      return "fas fa-scissors";
    case "laundry":
      return "fas fa-shirt";
    case "professional_service":
      return "fas fa-briefcase";
    case "contractor":
      return "fas fa-hammer";
    case "service":
      return "fas fa-handshake";
    case "residential":
      return "fas fa-home";
    case "office":
      return "fas fa-building";
    case "agricultural":
      return "fas fa-tractor";
    case "beach":
      return "fas fa-umbrella-beach";
    case "natural":
      return "fas fa-mountain";
    case "facility":
      return "fas fa-restroom";
    case "cemetery":
      return "fas fa-cross";
    case "event_venue":
      return "fas fa-calendar-days";
    case "marina":
      return "fas fa-anchor";
    case "planetarium":
      return "fas fa-globe";
    case "internet_cafe":
      return "fas fa-wifi";
    case "video_rental":
      return "fas fa-film";
    case "information":
      return "fas fa-circle-info";
    case "administrative":
      return "fas fa-map";
    case "locality":
      return "fas fa-map-pin";
    case "country":
      return "fas fa-flag";
    case "postal":
      return "fas fa-envelope";
    case "street":
      return "fas fa-road";
    case "address":
      return "fas fa-home";
    case "intersection":
      return "fas fa-road";
    case "district":
      return "fas fa-map-marked-alt";
    case "continent":
      return "fas fa-earth-americas";
    case "archipelago":
      return "fas fa-water";
    case "square":
      return "fas fa-square";
    case "poi":
      return "fas fa-location-dot";
    case "food":
      return "fas fa-utensils";
    case "health":
      return "fas fa-heart";
    case "political":
      return "fas fa-landmark";
    case "geocode":
    case "plus_code":
      return "fas fa-map-marked";
    case "industrial":
      return "fas fa-industry";
    default:
      return "fas fa-city";
  }
}

const ROOM_AMENITY_TYPES = {
  COFFEE_TEA: "coffeeTea",
  WIFI: "wifi",
  WORKSPACE: "workspace"
};

const ROOM_AMENITY_LABEL_KEYS = {
  [ROOM_AMENITY_TYPES.COFFEE_TEA]: "roomAmenityCoffeeTea",
  [ROOM_AMENITY_TYPES.WIFI]: "roomAmenityWifi",
  [ROOM_AMENITY_TYPES.WORKSPACE]: "roomAmenityWorkspace"
};

const ROOM_AMENITY_ICON_CLASSES = {
  [ROOM_AMENITY_TYPES.COFFEE_TEA]: "fas fa-mug-hot",
  [ROOM_AMENITY_TYPES.WIFI]: "fas fa-wifi",
  [ROOM_AMENITY_TYPES.WORKSPACE]: "fas fa-laptop"
};

const ROOM_AMENITY_MATCHERS = {
  [ROOM_AMENITY_TYPES.COFFEE_TEA]: [
    "coffee/tea maker",
    "coffee grinder",
    "electric kettle",
    "espresso maker",
    "free tea bags/instant coffee",
    "reusable coffee/tea filters",
    "koffiezetapparaat / waterkoker",
    "koffiemolen",
    "elektrische waterkoker",
    "espressoapparaat",
    "gratis thee/oploskoffie",
    "herbruikbare koffiefilter/theezeef"
  ],
  [ROOM_AMENITY_TYPES.WIFI]: [
    "free wifi",
    "free wifi (limited)",
    "free wired internet",
    "free dial-up internet access",
    "wifi (surcharge)",
    "wifi speed",
    "wireless internet access",
    "internet access",
    "internet access (complimentary)",
    "internet access (surcharge)",
    "high-speed internet access",
    "dial-up internet access",
    "dial-up internet access (surcharge)",
    "wired internet access (surcharge)",
    "limited wifi (surcharge)",
    "limited time for free wifi",
    "gratis wifi",
    "gratis wifi (beperkt",
    "gratis wifi (beperkte)",
    "gratis wifi (tijdelijke)",
    "gratis kabelinternet",
    "gratis inbelinternet",
    "gratis internet",
    "wifi (toeslag)",
    "wifisnelheid",
    "wifi",
    "internet",
    "snel internet",
    "internet (toeslag)",
    "inbelinternet",
    "inbelinternet (toeslag)",
    "kabelinternet (toeslag)",
    "beperkte wifi (toeslag)",
    "beperkte tijd voor gratis wifi"
  ],
  [ROOM_AMENITY_TYPES.WORKSPACE]: [
    "laptop-friendly workspace",
    "desk",
    "desk chair",
    "low-height desk",
    "laptopvriendelijke werkplek",
    "bureau",
    "bureaustoel",
    "verlaagd bureau"
  ]
};

const getRoomAmenityIcon = (amenityType) => {
  return ROOM_AMENITY_ICON_CLASSES[amenityType] || "fas fa-circle-info";
};

const getRoomAmenityLabelKey = (amenityType) => {
  return ROOM_AMENITY_LABEL_KEYS[amenityType] || null;
};

const flattenRoomAmenities = (value) => {
  if (!value) return [];

  if (typeof value === "string") {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => flattenRoomAmenities(entry));
  }

  if (typeof value === "object") {
    return Object.values(value).flatMap((entry) => flattenRoomAmenities(entry));
  }

  return [];
};

const normalizeAmenityValue = (value) => {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const hasRoomAmenityMatch = (amenities, amenityType) => {
  const matchers = ROOM_AMENITY_MATCHERS[amenityType] || [];

  return amenities.some((amenity) => {
    const normalizedAmenity = normalizeAmenityValue(amenity);
    return matchers.some((matcher) => normalizedAmenity.includes(matcher));
  });
};

const getRoomAmenities = (room) => {
  const rawAmenities = flattenRoomAmenities([
    room?.amenities,
    room?.roomAmenities,
    room?.amenitiesByType,
    room?.amenityData,
    room?.attributes,
    room?.features,
    room?.descriptionAmenities
  ]).filter((amenity) => typeof amenity === "string" && amenity.trim());

  const normalizedAmenities = [];

  Object.values(ROOM_AMENITY_TYPES).forEach((amenityType) => {
    if (hasRoomAmenityMatch(rawAmenities, amenityType)) {
      normalizedAmenities.push({
        type: amenityType,
        labelKey: getRoomAmenityLabelKey(amenityType),
        icon: getRoomAmenityIcon(amenityType)
      });
    }
  });

  return normalizedAmenities;
};

export default {
  getDateRange,
  nationalities,
  storageGetValue,
  storageSetValue,
  saveCompanyInfo,
  hasCompanyInfo,
  getCompanyInfo,
  getApiEnv,
  fetchCompanyInfo,
  getBusinessUnits,
  getCompanyAnnotations,
  getCompanyInfoSync,
  saveSession,
  clearStorage,
  saveToken,
  clearToken,
  formatNumber,
  getDateHash,
  formatDecimals,
  getLocationsFromSearch,
  formatPrice,
  stripHtml,
  invalidateSession,
  sessionIsValid,
  isLoggedIn,
  hasToken,
  getToken,
  GEO_LOCATION_TYPES,
  PAGES,
  getApiURL,
  isProd,
  MeetingRoomSelectables,
  getRoomAmenities,
  getRoomAmenityIcon,
  getRoomAmenityLabelKey
};
